import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, Heart, Sparkles, Calendar, Briefcase, Users, Phone, ArrowRight } from 'lucide-react';

const MENU_ITEMS = [
  { label: 'Home', path: '/', icon: Home },
  { label: 'Wedding', path: '/wedding', icon: Heart },
  { label: 'Fashion', path: '/fashion', icon: Sparkles },
  { label: 'Events', path: '/events', icon: Calendar },
  { label: 'Commercial', path: '/commercial', icon: Briefcase },
  { label: 'About Us', path: '/about', icon: Users },
  { label: 'Contact Us', path: '/contact', icon: Phone },
];

export default function MobileSidebar({ open, onClose }) {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            onClick={onClose}
            data-testid="sidebar-backdrop"
          />

          {/* Sidebar */}
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'tween', duration: 0.35, ease: 'easeInOut' }}
            data-testid="mobile-sidebar"
            className="fixed left-0 top-0 bottom-0 w-[300px] bg-visyra-bg border-r border-white/5 z-[70] flex flex-col"
          >
            {/* Close Button */}
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <Link to="/" onClick={onClose} className="flex items-baseline gap-0">
                <span className="font-heading text-gold text-2xl italic tracking-[0.15em]">V</span>
                <span className="font-heading text-visyra-text text-xl tracking-[0.15em]">isyra</span>
              </Link>
              <button
                onClick={onClose}
                data-testid="sidebar-close-btn"
                className="text-visyra-muted hover:text-gold transition-colors duration-300"
              >
                <X size={22} />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 px-4 py-4 overflow-y-auto">
              {MENU_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    className={`flex items-center gap-4 px-4 py-4 rounded-sm border-b border-white/5 transition-all duration-300 ${
                      isActive(item.path)
                        ? 'text-gold bg-gold-subtle'
                        : 'text-visyra-muted hover:text-gold hover:bg-gold-subtle/50'
                    }`}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                    <span className="font-body text-base tracking-wide">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Bottom CTA */}
            <div className="p-5 border-t border-white/5">
              <div className="bg-visyra-card/80 border border-white/10 rounded-lg p-5 mb-4 text-center">
                <p className="font-heading italic text-lg text-visyra-text leading-snug" data-testid="sidebar-cta-text">
                  We'd love to hear<br />your story!
                </p>
              </div>
              <Link
                to="/contact"
                onClick={onClose}
                data-testid="sidebar-enquire-btn"
                className="flex items-center justify-center gap-2 w-full bg-gold text-visyra-bg font-body text-sm font-medium uppercase tracking-widest py-3.5 rounded-full hover:bg-gold-light transition-colors duration-300"
              >
                Enquire <ArrowRight size={14} />
              </Link>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
